/**
 * Turns the onboarding answers into the shape the results engine consumes:
 *   { [slug]: { score, maxScore, percentage, riskLevel, basis } }
 *
 * Only the primary area is screened (six questions, 0–24), exactly as in the
 * production app: secondary areas are never screened, and an area already in
 * recovery is never screened at all. Those still need a number for the
 * readout, so they get an ESTIMATE, and every entry carries a `basis` string
 * the UI prints next to the figure. Nothing here invents a clinical claim: an
 * estimate is labelled as one wherever it is shown.
 */
import { SCREENER_MAX, BARC10_MAX, BARC10_QUESTIONS, buildScreener } from '../data/onboarding'
import { recencyMult } from './priority'

/** Percentage → band, matching the production thresholds. */
export function riskLevelFor(percentage) {
  if (percentage >= 75) return 'severe'
  if (percentage >= 50) return 'high'
  if (percentage >= 25) return 'moderate'
  return 'low'
}

export const BASIS = {
  screened: 'from your six answers',
  gate: 'estimated from how often and how controllable',
  recovery: 'estimated from time in recovery',
}

// An active area that was never screened: intensity from the frequency answer,
// scaled by how controllable it feels. Daily + "No" ≈ 75%, monthly + "Yes" ≈ 7%.
const FREQ_INTENSITY = { 1.3: 0.75, 1.15: 0.5, 1.05: 0.4, 1.0: 0.15 }
const CONTROL_FACTOR = { 1.3: 1.0, 1.15: 0.85, 1.05: 0.6, 1.0: 0.45 }

function estimateActive(gate) {
  const f = FREQ_INTENSITY[gate?.frequency] ?? 0.5
  const c = CONTROL_FACTOR[gate?.control] ?? 0.85
  return Math.round(Math.min(100, f * c * 100))
}

// An area in recovery: the recency multiplier already encodes how much weight
// the program gives it (1.4 the day you stop → 0.3 after five years). Rescaled
// so the day you stop reads as moderate load (50) and long recovery falls to a
// floor of 5. It deliberately never reaches the "high" band: someone who has
// stopped is carrying decaying load, not active risk, and a readout that told
// them otherwise would be both wrong and discouraging.
function estimateRecovery(gate) {
  const m = recencyMult(gate?.daysClean ?? 0)
  return Math.round(Math.max(5, Math.min(49, ((m - 0.3) / 1.1) * 44 + 5)))
}

/**
 * @param {object} p
 * @param {string[]} p.selected            selected slugs
 * @param {Record<string, object>} p.gates slug → { status, frequency, control, daysClean }
 * @param {string|null} p.primary          the chosen primary slug
 * @param {Record<number, number>} p.screener  question index → chosen option index
 */
export function buildAreaResults({ selected, gates, primary, screener }) {
  const out = {}
  selected.forEach((slug) => {
    const gate = gates[slug]
    const isPrimary = slug === primary
    const screened = isPrimary && gate?.status === 'active' && screener && Object.keys(screener).length > 0

    if (screened) {
      const questions = buildScreener(slug)
      let score = 0
      questions.forEach((q, i) => {
        const chosen = screener[i]
        if (chosen != null && q.answers[chosen]) score += q.answers[chosen].score
      })
      // Percentage of the six answers' own maximum (24). NOT the production
      // number: the backend divides this sum by a legacy per-instrument maximum
      // (alcohol 40, nicotine 16, gambling 27, pornography 126 …), which no
      // six-item sum can reach. This is the honest conference reading.
      const percentage = Math.round((score / SCREENER_MAX) * 100)
      out[slug] = {
        score,
        maxScore: SCREENER_MAX,
        percentage,
        riskLevel: riskLevelFor(percentage),
        basis: BASIS.screened,
        estimated: false,
      }
      return
    }

    const inRecovery = gate?.status === 'recovery'
    const percentage = inRecovery ? estimateRecovery(gate) : estimateActive(gate)
    out[slug] = {
      score: Math.round((percentage / 100) * SCREENER_MAX),
      maxScore: SCREENER_MAX,
      percentage,
      riskLevel: riskLevelFor(percentage),
      basis: inRecovery ? BASIS.recovery : BASIS.gate,
      estimated: true,
    }
  })
  return out
}

/**
 * BARC-10: sum of the ten Likert answers (0–6 each, max 60), placed on the
 * product's support-tier cut-points from backend config/onboarding/barc10.json:
 *   Light ≥ 47 · Standard ≥ 37 · Intensive ≥ 27 · Supported < 27
 *
 * The product applies those to a CONTEXT-ADJUSTED score (raw plus an adjustment
 * of −6…0 derived from recovery length, prior attempts and whether the person
 * changed environment). The booth flow drops the two context questions, so this
 * scores the raw total and can land one tier high for someone who would have
 * taken a penalty. `adjusted: false` records that.
 */
export function scoreBarc10(answers) {
  const values = BARC10_QUESTIONS.map((q) => answers?.[q.id]).filter((v) => v != null)
  if (values.length === 0) return null
  const total = values.reduce((a, b) => a + b, 0)
  const percentage = Math.round((total / BARC10_MAX) * 100)
  const tier = total >= 47 ? 'Light' : total >= 37 ? 'Standard' : total >= 27 ? 'Intensive' : 'Supported'
  return { total, max: BARC10_MAX, percentage, tier, answered: values.length, adjusted: false }
}

/** Days clean for the primary, for the readout header. */
export function daysCleanFor(gates, primary) {
  const g = primary ? gates[primary] : null
  return g?.status === 'recovery' ? g.daysClean ?? 0 : null
}
